import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { OBJECTIVE_SCALE, scaleAt, type ZoomScale } from '../../viewer/zoomLimits';

interface Props {
  magnification: number | null;
  onSelect: (m: number) => void;
  previewAt: (m: number) => Promise<string | null>;
  /** What it reads (zoomLimits): a slide's objective magnification unless told. */
  scale?: ZoomScale;
  /** Hovering shows the image at that magnification; not where the tiles are
   *  no pictures the browser decodes (an X-ray), which shows the number alone. */
  previews?: boolean;
}

// The scale, log-spaced. Zooming in goes on past its top to its cap: the
// indicator then sits at the top and the badge reads the real magnification.
const H = 150;
const PAD = 12; // vertical inset so 1× / 100× aren't flush against the border
const INNER = H - 2 * PAD;
export const LENS_WIDTH = 58;
const W = LENS_WIDTH;
const TRACK_X = W - 6;

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
const fracOf = (s: ZoomScale, m: number) =>
  Math.log(Math.min(s.max, Math.max(s.min, m)) / s.min) / Math.log(s.max / s.min);
const yOf = (s: ZoomScale, m: number) => PAD + (1 - fracOf(s, m)) * INNER;
const magOf = (s: ZoomScale, y: number) => scaleAt(s, clamp01(1 - (y - PAD) / INNER));
/** A pointer's y on the scale, given the lens's top on screen. */
const yOnScale = (top: number, clientY: number) => Math.min(H - PAD, Math.max(PAD, clientY - top));

function fmt(m: number, s: ZoomScale): string {
  return `${fmtNumber(m)}${s.unit}`;
}

function fmtNumber(m: number): string {
  if (m >= 10) return `${Math.round(m)}`;
  if (m >= 1) return `${Math.round(m * 10) / 10}`;
  return `${Math.round(m * 100) / 100}`;
}

/**
 * The magnification a typed entry asks for, or null when it is not one: a
 * plain decimal, with the scale's unit after it ("40x", "40×"; "200%" on an
 * X-ray's) or not. A comma is
 * a decimal point ("2,5"), except before exactly three digits, where it groups
 * thousands ("1,000"). Nothing else is a number here: not "0x10", not "1e3".
 * The scale's cap holds the top, and the engine holds the bottom at the
 * entire image.
 */
export function parseMagnification(text: string, scale: ZoomScale = OBJECTIVE_SCALE): number | null {
  const digits = text.trim().replace(scale.unit === '%' ? /\s*%$/ : /\s*[x×]$/i, '');
  if (!/^\d+([.,]\d+)?$/.test(digits)) return null;
  const m = Number(digits.replace(/,(\d{3})$/, '$1').replace(',', '.'));
  return m > 0 ? Math.min(scale.cap, m) : null;
}

// The badge, and the entry that takes its place: sized to sit just left of
// the ticks, aligned with their numbers, and kept inside the housing.
const BADGE_H = 16;
const ENTRY_W = 34;
const ENTRY_X = TRACK_X - 16 - ENTRY_W;
const badgeTop = (y: number) => Math.min(H - BADGE_H, Math.max(0, y - BADGE_H / 2));
// Two touches this close together, in time and on the scale, are a double-tap.
const DOUBLE_TAP_MS = 350;
const DOUBLE_TAP_PX = 12;

export function MagnificationLens({ magnification, onSelect, previewAt, scale = OBJECTIVE_SCALE, previews = true }: Props) {
  const [hoverY, setHoverY] = useState<number | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const urlRef = useRef<string | null>(null);
  const reqRef = useRef(0);

  // Debounced preview fetch for the hovered magnification.
  useEffect(() => {
    if (hoverY == null || !previews) {
      setPreview(null);
      return;
    }
    const m = magOf(scale, hoverY);
    const id = ++reqRef.current;
    const t = setTimeout(async () => {
      const url = await previewAt(m);
      if (id !== reqRef.current) {
        if (url) URL.revokeObjectURL(url);
        return; // stale
      }
      setPreview(url);
    }, 90);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoverY, previews, scale]);

  useEffect(() => () => setPreview(null), []);

  function setPreview(url: string | null) {
    if (urlRef.current) URL.revokeObjectURL(urlRef.current);
    urlRef.current = url;
    setPreviewUrl(url);
  }

  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (dragging.current) return; // the window listener owns the position mid-drag
    setHoverY(yOnScale(e.currentTarget.getBoundingClientRect().top, e.clientY));
  };

  // A double-click (or double-tap) on the scale opens an entry where it
  // landed, holding the magnification the two clicks already zoomed to; type a
  // number, Enter.
  const [entry, setEntry] = useState<{ text: string; y: number } | null>(null);
  const entryDone = useRef(false);
  const openEntry = (y: number) => {
    if (magnification == null) return; // no zoom range to enter into yet
    entryDone.current = false;
    setEntry({ text: fmtNumber(magOf(scale, y)), y });
  };
  // Enter applies, Escape drops it, and clicking away applies what was typed.
  // Whichever comes first closes it; the blur that follows does nothing more.
  const finishEntry = (apply: boolean) => {
    if (entryDone.current || !entry) return;
    entryDone.current = true;
    const m = apply ? parseMagnification(entry.text, scale) : null;
    if (m != null) onSelect(m);
    setEntry(null);
  };
  // The entry is for the magnification it opened on: gone with it (the slide
  // swapped, or the engine torn down, while typing).
  useEffect(() => {
    if (magnification == null && entry) {
      entryDone.current = true;
      setEntry(null);
    }
  }, [magnification, entry]);

  const onDoubleClick = (e: React.MouseEvent<SVGSVGElement>) =>
    openEntry(yOnScale(e.currentTarget.getBoundingClientRect().top, e.clientY));

  // Grab the indicator and run it up and down the scale — the magnification
  // follows continuously, so you can hunt for a level instead of aiming a click.
  const dragging = useRef(false);
  const lastTap = useRef<{ at: number; y: number } | null>(null);
  const onPointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
    // Keeps the pointer's focus where it is, so an open entry would stay
    // focused and keep its stale number while this press zooms elsewhere:
    // the press drops it, and its own zoom is what the person gets.
    e.preventDefault();
    finishEntry(false);
    const top = e.currentTarget.getBoundingClientRect().top;
    dragging.current = true;
    const apply = (clientY: number) => {
      const y = yOnScale(top, clientY);
      setHoverY(y);
      onSelect(magOf(scale, y));
    };
    apply(e.clientY);
    const move = (ev: PointerEvent) => apply(ev.clientY);
    const up = () => {
      dragging.current = false;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    // A touch never sends dblclick (iOS never does, and touch-action: none
    // takes it from Chrome), so a second tap close to the first opens the entry.
    if (e.pointerType === 'touch') {
      const y = yOnScale(top, e.clientY);
      const last = lastTap.current;
      const now = e.timeStamp;
      lastTap.current = { at: now, y };
      if (last && now - last.at < DOUBLE_TAP_MS && Math.abs(y - last.y) < DOUBLE_TAP_PX) {
        lastTap.current = null;
        openEntry(y);
      }
    }
  };

  const curY = magnification != null ? yOf(scale, magnification) : null;
  const hoverMag = hoverY != null ? magOf(scale, hoverY) : null;

  return (
    <div style={{ position: 'relative', pointerEvents: 'auto' }}>
      {/* Hover preview popup, to the left of the lens: the number alone without previews */}
      {hoverY != null && (
        <div
          className={cn('pointer-events-none absolute rounded-md p-[3px] shadow-md', !previews && 'whitespace-nowrap px-1.5')}
          style={{
            right: W + 8,
            top: previews ? Math.min(H - 100, Math.max(0, hoverY - 50)) : Math.min(H - 22, Math.max(0, hoverY - 11)),
            width: previews ? 116 : undefined,
            background: 'rgba(0,0,0,0.8)',
          }}
        >
          {previews && (
            <div className="flex h-21 w-[110px] items-center justify-center overflow-hidden rounded-sm bg-neutral-900">
              {previewUrl ? (
                <img src={previewUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                <span className="text-xs text-muted-foreground">…</span>
              )}
            </div>
          )}
          <div className="text-center text-[11px] font-bold text-white">
            {hoverMag != null ? fmt(hoverMag, scale) : ''}
          </div>
        </div>
      )}

      <svg
        width={W}
        height={H}
        onMouseMove={onMove}
        onMouseLeave={() => !dragging.current && setHoverY(null)}
        onPointerDown={onPointerDown}
        onDoubleClick={onDoubleClick}
        style={{ display: 'block', cursor: 'ns-resize', overflow: 'visible', touchAction: 'none' }}
      >
        {/* dark housing so the lens reads on any slide background (incl. white) */}
        <rect x={0} y={0} width={W} height={H} rx={7} fill="rgba(0,0,0,0.6)" />
        {/* axis */}
        <line x1={TRACK_X} y1={PAD} x2={TRACK_X} y2={H - PAD} stroke="rgba(255,255,255,0.85)" strokeWidth={1.5} />
        {/* ticks (lens lines) */}
        {scale.ticks.map((m) => {
          const y = yOf(scale, m);
          return (
            <g key={m}>
              <line
                x1={TRACK_X - 15}
                y1={y}
                x2={TRACK_X}
                y2={y}
                stroke="#fff"
                strokeWidth={2}
              />
              <text
                x={TRACK_X - 19}
                y={y + 3}
                fontSize={9}
                fontWeight={600}
                fill="#fff"
                textAnchor="end"
              >
                {scale.unitOnTicks ? `${m}${scale.unit}` : m}
              </text>
            </g>
          );
        })}

        {/* hover marker */}
        {hoverY != null && (
          <line
            x1={0}
            y1={hoverY}
            x2={TRACK_X}
            y2={hoverY}
            className="stroke-blue-500"
            strokeWidth={1}
            strokeDasharray="2 2"
          />
        )}

        {/* current magnification indicator: line + a solid badge that reads on top */}
        {curY != null &&
          (() => {
            const label = magnification != null ? fmt(magnification, scale) : '';
            const bh = BADGE_H;
            const bw = label.length * 6.6 + 9;
            // Just left of the ticks, aligned with the numbers; a long one ("2400%") stays in the housing.
            const bx = Math.max(0, TRACK_X - 16 - bw);
            const by = badgeTop(curY);
            return (
              <g>
                {/* The image list's blue (blue-500), the viewer's one "current" colour (user, 2026-09-26). */}
                <line x1={0} y1={curY} x2={TRACK_X} y2={curY} className="stroke-blue-500" strokeWidth={2} />
                <circle cx={TRACK_X} cy={curY} r={3.5} className="fill-blue-500" />
                {/* The entry stands in for the badge while it is open. */}
                {!entry && (
                  <>
                    <rect
                      x={bx}
                      y={by}
                      width={bw}
                      height={bh}
                      rx={3}
                      fill="rgba(8,10,14,0.95)"
                      className="stroke-blue-500"
                      strokeWidth={1.5}
                    />
                    <text
                      x={bx + bw / 2}
                      y={by + bh / 2 + 3.6}
                      fontSize={11}
                      fontWeight={700}
                      fill="#fff"
                      textAnchor="middle"
                    >
                      {label}
                    </text>
                  </>
                )}
              </g>
            );
          })()}
      </svg>

      {/* The typed magnification, in the badge's place at the double-click. */}
      {entry && (
        <input
          aria-label={scale.name}
          autoFocus
          value={entry.text}
          inputMode="decimal"
          onChange={(e) => setEntry({ ...entry, text: e.target.value })}
          onFocus={(e) => e.currentTarget.select()}
          onKeyDown={(e) => {
            // Ours alone: the page's digit keys zoom, and other keys arm tools.
            e.stopPropagation();
            if (e.key === 'Enter') finishEntry(true);
            else if (e.key === 'Escape') finishEntry(false);
          }}
          onBlur={() => finishEntry(true)}
          className="absolute rounded-[3px] border-blue-500 bg-[rgba(8,10,14,0.95)] p-0 text-center text-[11px] font-bold text-white outline-none"
          style={{
            left: ENTRY_X,
            top: badgeTop(entry.y),
            width: ENTRY_W,
            height: BADGE_H,
            borderWidth: 1.5,
            borderStyle: 'solid',
          }}
        />
      )}
    </div>
  );
}
